import React from 'react';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useMount, useUnmount } from 'react-use';
import { getWindow } from '@surfline/web-common';

import { goToUsersNearestSpot, setMapLocation } from 'actions/map';
import MapPageMeta from 'components/MapPageMeta';
import PageLoading from 'components/PageLoading';
import { getOrCreateStore, type ReduxInitialProps } from 'stores';
import { useAppDispatch, useAppSelector } from 'stores/hooks';

const SYNC_URL = true;

// Type guard function to check if location has valid coordinates
const isValidLocation = (
  location: any,
): location is { center: { lat: number; lon: number }; zoom: number } =>
  location &&
  location.center &&
  typeof location.center.lat === 'number' &&
  typeof location.center.lon === 'number' &&
  location.center.lat !== 0 &&
  location.center.lon !== 0 &&
  location.center.lat !== null &&
  location.center.lon !== null &&
  typeof location.zoom === 'number' &&
  !Number.isNaN(location.center.lat) &&
  !Number.isNaN(location.center.lon) &&
  !Number.isNaN(location.zoom);

const SpotMapPage: NextPage<{}, ReduxInitialProps> = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();

  // Get current map location from store to avoid unnecessary API calls
  const currentMapLocation = useAppSelector((state) => state?.map?.location);

  // When the map page is mounted under this route, we want to redirect it to the map page with the
  // location in the url. This logic will fire on mount, and will redirect the user to the router with
  // the location in the url.
  useMount(() => {
    const win = getWindow();
    if (win && router.isReady) {
      win.document.body.classList.add('sl-hide-ze-widget', 'sl-map-page');

      // Check if we already have valid coordinates in the store (from SSR or previous navigation)
      if (isValidLocation(currentMapLocation)) {
        // Use existing store location and sync to URL
        dispatch(setMapLocation(currentMapLocation, router, SYNC_URL));
        return;
      }

      // Check if we have location in the current URL hash/query
      // This can happen during client-side navigation
      const urlParams = new URLSearchParams(win.location.search);
      const locationParam = urlParams.get('location') || win.location.hash.replace('#', '');

      if (locationParam) {
        // If URL contains location info, prioritize that
        try {
          const [lat, lon, zoomStr] = locationParam.split(',');
          const zoom = Number(zoomStr?.replace('z', ''));

          if (!Number.isNaN(Number(lat)) && !Number.isNaN(Number(lon)) && !Number.isNaN(zoom)) {
            const locationObj = {
              center: {
                lat: Number(lat),
                lon: Number(lon),
              },
              zoom,
            };

            // Clear session storage to prevent conflicts
            try {
              win.sessionStorage.removeItem('slMapLocation');
            } catch (e) {
              console.error('Failed to clear session storage:', e);
            }

            dispatch(setMapLocation(locationObj, router, SYNC_URL));
            return;
          }
        } catch (e) {
          console.error('Failed to parse URL location:', e);
        }
      }

      // Check session storage as fallback
      const storageItem = win.sessionStorage.getItem('slMapLocation');
      if (storageItem) {
        try {
          const sessionLocation = JSON.parse(storageItem);
          if (isValidLocation(sessionLocation)) {
            dispatch(setMapLocation(sessionLocation, router, SYNC_URL));
            return;
          }
        } catch (e) {
          console.error('Failed to parse session location:', e);
        }
      }

      // Only call goToUsersNearestSpot as last resort
      dispatch(goToUsersNearestSpot(router, SYNC_URL, false));
    }
  });

  useUnmount(() => {
    const win = getWindow();
    if (win) {
      win.document.body.classList.remove('sl-hide-ze-widget', 'sl-map-page');
    }
  });

  return (
    <>
      <MapPageMeta mapMode="surf" />
      <PageLoading />
    </>
  );
};

SpotMapPage.getInitialProps = async (context) => {
  const store = getOrCreateStore();

  const isServer = !!context.req;

  if (isServer) {
    return {
      isAppBannerEnabled: true,
      isFooterVisible: false,
      ssrReduxState: store.getState(),
    };
  }

  return {
    isAppBannerEnabled: true,
    isFooterVisible: false,
  };
};

export default SpotMapPage;
